export const HEADER_HEIGHT = 88
export const SIDEBAR_HEADER_HEIGHT = 88

/*
 * NO `SIDEBAR_FOOTER_HEIGHT`. It was `72`, it was imported by nothing, and the block it
 * claimed to measure — Help / Settings / Labs, the divider, the reader row and the account
 * chip — measures 261px at 1280x800 and 1280x640. Wrong by 189px, i.e. 3.6x.
 *
 * Deleted rather than corrected, because a correct number here would be the more dangerous
 * artifact: the sidebar's nav list takes the leftover column space with `flex: 1` (see the
 * nav Box in `Sidebar.tsx`), so the footer's REAL rendered height is what gets subtracted,
 * whatever it turns out to be. Nothing needs the figure, and a constant that agrees with
 * layout today is precisely the thing that silently stops agreeing later — `LABS_HEADER_H`
 * said 64 against a measured 93 and clipped the Labs sub-nav for exactly that reason
 * (4a3f0619c, which replaced it with a `useLayoutEffect` measurement).
 *
 * This one had not clipped anything yet ONLY because no one had imported it. If a future
 * consumer needs the footer's height, measure it — do not reintroduce a literal.
 */

/**
 * VRG-490 round 2 — widened from 256, reported as "reads far too narrow"
 * (desktop-only feedback; the collapsed rail, `SIDEBAR_MINIMIZED_WIDTH`, is
 * unaffected). This is the ONE place the expanded rail's width is declared —
 * every consumer (`SidebarList`, `SidebarSearchTrigger`, `SidebarCollapseToggle`,
 * `SidebarHelpPopover`, `SidebarNestedList`, `EncodingDeviceSidebarButton`)
 * reads it as `SIDEBAR_WIDTH - <inset>`, so changing it here alone widens the
 * whole rail consistently. Does not affect `SIDEBAR_HEADER_HEIGHT` or the
 * page-header logo datum (§29) — that alignment is height-only and independent
 * of this constant.
 */
export const SIDEBAR_WIDTH = 288
export const SIDEBAR_MINIMIZED_WIDTH = 80

/**
 * Readable-measure cap for a centred single-column page — settings sections, and any
 * page that is prose or a form rather than a data table.
 *
 * 1100px is roughly where a two-column form stops gaining anything from more width, and
 * it keeps description paragraphs near a comfortable line length: at full width on a
 * wide display they ran the better part of a metre.
 *
 * PROMOTED HERE from a private const in `SettingsPage` when What's new needed the same
 * measure. Two pages hard-coding the same width is how the two stop agreeing — the
 * second copy gets tuned, the first does not, and nobody notices until they are seen
 * side by side. Any further centred page should read this rather than pick its own.
 */
export const NARROW_COLUMN_WIDTH = 1100

/**
 * CSS custom property carrying the Ask panel's current width, or 0px when it is closed.
 *
 * The panel is a flex sibling of the page, so ordinary content reflows around it for
 * free. `position: fixed` elements do not — they are laid out against the viewport and
 * cannot see that the page got narrower. The handful of full-bleed save bars in the app
 * are exactly that, so they read this variable instead. AskPanel is the only writer.
 */
export const ASK_PANEL_WIDTH_VAR = '--ask-panel-width'

/**
 * Width for a fixed bar that should span the page area — everything except the sidebar
 * on the left and the Ask panel on the right.
 *
 * A function rather than a constant because six pages need it, and the arrangement it
 * replaces (each page inlining `calc(100% - 80px)`) is exactly the kind of duplicated
 * layout rule that goes stale one page at a time. Takes the offset the bar is positioned
 * at, so a bar sitting at SIDEBAR_WIDTH and one at SIDEBAR_MINIMIZED_WIDTH each stay in
 * agreement with their own `left`.
 */
export const fullBleedBarWidth = (leftOffset: number = SIDEBAR_MINIMIZED_WIDTH) =>
  `calc(100% - ${leftOffset}px - var(${ASK_PANEL_WIDTH_VAR}, 0px))`

/**
 * Height of one sidebar row (nav item, search trigger, collapse toggle).
 *
 * Promoted here from three separate local literals (`height: 48` in
 * SidebarSearchTrigger and SidebarCollapseToggle, `const ROW_HEIGHT = 48` in
 * EncodingDeviceSidebarButton) when a fourth consumer appeared — the nav list's
 * minimum-height floor, which is expressed in rows and would silently stop
 * meaning "two rows" if any copy drifted.
 *
 * LAYOUT px, not painted px: the app-wide text zoom (hooks/useTextZoom) scales
 * the rendered result, so this stays 48 at every text size and anything derived
 * from it scales along with the rest of the chrome.
 *
 * TODO(VRG-248 / TD): converge the three existing literals onto this constant
 * next time those components are touched.
 */
export const SIDEBAR_ROW_HEIGHT = 48

/**
 * The `<ListItem>` inset in the sidebar's nav list — its `pl`/`pr`, and (as
 * `SIDEBAR_ROW_GUTTER` below) its `pt`/`pb`.
 *
 * 12px, i.e. `spacing(3)` on the platform's 4px base (§29). Named rather than left as a
 * `pl: 3` literal because `SIDEBAR_ROW_CONTENT_LEFT` is derived from it and a silent change
 * to one would misalign the other.
 */
export const SIDEBAR_ROW_INSET = 12

/**
 * The `<ListItemButton>`'s own left padding inside that inset.
 *
 * 16px. This was MUI's implicit `ListItemButton` default (`8px 16px`) and was therefore
 * written down NOWHERE, while the section label's left edge had to agree with it. That is
 * how the label ended up 16px adrift: the label was set to 12px against the assumption it
 * lined up with the row's content, and nothing tied the two numbers together. `SidebarList`
 * now sets this explicitly — visually a no-op, but it turns an inherited default into a
 * stated one that `SIDEBAR_ROW_CONTENT_LEFT` can be derived from.
 */
export const SIDEBAR_ROW_BUTTON_INSET = 16

/**
 * Where a nav row's CONTENT starts — the left edge of its icon, 28px from the rail's edge.
 *
 * This is the edge a section heading aligns to (VRG-… / the ACCOUNTS-COMMERCE misalignment).
 * The alternative was the row's TEXT at 64px, and it loses: the icon is the row's first ink,
 * so the icon column IS the group's left edge, and a heading set at 64 would sit almost a
 * quarter of the way across a 288px rail with nothing above or below it at that edge. It
 * also matches the platform's only other label-over-an-indented-list — the settings nav,
 * whose heading sits at its rail's content start (measured 324 against an icon at 328 and a
 * label at 364).
 */
export const SIDEBAR_ROW_CONTENT_LEFT = SIDEBAR_ROW_INSET + SIDEBAR_ROW_BUTTON_INSET

/** The `<ListItem>`'s vertical padding — the invisible strip between a row's painted surface and its neighbour's. */
export const SIDEBAR_ROW_GUTTER = 4

/**
 * Height of a section-boundary slot in the internal sidebar's nav list — the box that holds
 * a `NavSectionLabel` heading, a `<Divider>` rule, or (expanded) both.
 *
 * FIXED and IDENTICAL in both rail states, which is the entire point: before this existed,
 * the heading (~27px, text + padding) and the rule (~9px, a hairline + margin) had
 * different heights, so every row below a section boundary jumped up or down by the
 * difference whenever the sidebar was opened or closed — measured 18-19px per boundary,
 * three boundaries in the internal menu, enough to visibly relocate half the rail on every
 * toggle. `SidebarList` renders every boundary inside a `height: SIDEBAR_SECTION_HEIGHT`
 * box, so the box itself — not its content — decides how much vertical space a boundary
 * claims, and that decision doesn't change with `sidebarOpen`.
 *
 * ## THE CONTENT IS CENTRED, and that single fact settles three separate requirements
 *
 * Everything in the slot — the heading's line box and the rule alike — is `alignItems:
 * center`. That is not one choice repeated; it is the ONLY arrangement that satisfies all
 * three of the constraints the boundary is under simultaneously:
 *
 *   1. The heading must sit exactly where the collapsed rule sits, so toggling the rail
 *      moves no ink. Both are centred in the same fixed-height box, so their centrelines
 *      coincide BY CONSTRUCTION rather than by two numbers that happen to agree.
 *   2. The heading must be vertically centred between its neighbouring rows. A centred line
 *      box in a slot whose neighbours are equidistant from it (`SIDEBAR_ROW_GUTTER` on both
 *      sides) is centred between them.
 *   3. Expanded, the rule runs on from the heading at the heading's own centreline — which
 *      is only coherent if the heading IS on the centreline.
 *
 * Requirements 1 and 2 look independent and could have fought: pin the heading to the rule's
 * y and its gaps fall where they fall. They do not fight, because centring is the fixed
 * point of both.
 *
 * ## What the gaps come out at, and why the slot did NOT have to grow
 *
 * Measured from the neighbouring rows' PAINTED edges (the `ListItemButton` surface — not the
 * `<ListItem>`, whose `SIDEBAR_ROW_GUTTER` is invisible) to the heading's line box:
 *
 *   gap = ROW_GUTTER + (SIDEBAR_SECTION_HEIGHT - labelLineBox) / 2
 *       = 4          + (36 - 15.4) / 2                          = 14.3px, both sides
 *
 * where `labelLineBox` is the heading's own 11px x 1.4 line height.
 *
 * An earlier pass capped the gap BELOW a heading at 8px, reasoning that rows inside a group
 * sit `2 * SIDEBAR_ROW_GUTTER` = 8px apart and a heading further from its own first row than
 * that would read as detached. Centring necessarily breaks that cap, so the open question
 * was whether the slot had to grow to keep both gaps above 8 — it does not. At the existing
 * 36 both gaps land at 14.3, comfortably clear of the 8px interior rhythm in both directions,
 * so the boundary still reads as a boundary on each side. The cost of centring is zero: the
 * slot's height, and therefore the 108px the three boundaries claim in a list that already
 * scrolls at 1280x800, is unchanged.
 *
 * The residual is sub-pixel and worth stating so nobody "fixes" it: Lato's ascent and descent
 * are not symmetric about the line box, so the text's INK centre sits ~0.2px above the line
 * box's centre, and its CAP centre ~0.2px below. Both are inside a rounding error of the
 * rule; no optical nudge is applied, and applying one would break requirement 1's exactness
 * to buy nothing visible.
 */
export const SIDEBAR_SECTION_HEIGHT = 36

/**
 * Gap between a section heading and the rule that runs on from it to the rail's right edge.
 *
 * Expanded, the boundary is `LABEL ──────────`: the same rule the collapsed rail shows, on
 * the same centreline, with the heading occupying its left end. 12px is `SIDEBAR_ROW_INSET`,
 * i.e. the rail's own inset re-used as the gap — the label needs to be clear of the rule
 * without the rule looking like it belongs to a different element.
 */
export const SIDEBAR_SECTION_RULE_GAP = SIDEBAR_ROW_INSET
